import { debounce } from "throttle-debounce";
import $, { Cash } from "cash-dom";

/**
 * Repository Module
 * Handles interactions and visibility based on viewport position.
 * @package KAA
 * @author Tim Jungmann <Tim@Humans-Machines.com>
 */
export default class People {
    /**
     * Singleton instance of the component
     */
    static #instance: People;

    /**
     * Cash body reference
     */
    protected $body: Cash;

    /**
     * Stores timeout IDs for hover removal
     */
    protected hoverTimeouts: Map<HTMLElement, number> = new Map();

    /**
     * UI references
     */
    protected ui = {
		teaser: '.js-teaser',
        teaserDesc: ".js-teaser-desc",
        descToggle: ".js-desc-toggle",
    };

    /**
     * Repository constructor
     */
    protected constructor() {
        this.$body = $("body");
        this.registerEvents();
    }

    /**
     * Returns the singleton instance of the component
     */
    public static getInstance(): People {
        if (!People.#instance) {
            People.#instance = new People();
        }

        return People.#instance;
    }

    /**
     * Register required DOM and PubSub events
     */
	protected registerEvents(): void {
		this.$body.on("click", `${this.ui.descToggle}, ${this.ui.teaserDesc}`, this.toggleDescription.bind(this));
	}


    /**
     * Set initial sorting of search results once the site is loaded
     */
	protected toggleDescription(event: Event): void {
		event.preventDefault();

		const $target = $(event.currentTarget as HTMLElement);
		const $teaser = $target.closest(this.ui.teaser);
		const $toggle = $teaser.find(this.ui.descToggle);
		const $desc = $teaser.find(this.ui.teaserDesc);
		const isDescClick = $target.is(this.ui.teaserDesc);

		if (!$teaser.length) return;

		// Close if clicking on `.js-teaser-desc`
		if (isDescClick) {
			this.closeTeaser($teaser);
			return;
		}

		// Close all other expanded teasers before opening the new one
		this.$body.find(`${this.ui.teaser}.is-active`).not($teaser)
			.each((_, otherTeaser) => this.closeTeaser($(otherTeaser)));

		// Toggle the clicked teaser
		const isExpanded = $toggle.attr("aria-expanded") === "true";
		$teaser.toggleClass("is-active", !isExpanded);
		$toggle.attr("aria-expanded", String(!isExpanded));

		// Shift focus to description when opening & handle blur
		if (!isExpanded) {
			const descEl = $desc.get(0);
			if (descEl) {
				descEl.setAttribute("tabindex", "-1");
				descEl.focus({ preventScroll: true });
			}

			// Handle blur to close when focus is lost
			$desc.off("blur").on("blur", () => {
				this.closeTeaser($teaser);
			});
		}
	}

	/**
	 * Closes a given teaser, removing active state and resetting aria attributes
	 */
	protected closeTeaser($teaser: Cash): void {
		if (!$teaser.length) return;

		$teaser.removeClass("is-active");
		$teaser.find(this.ui.descToggle).attr("aria-expanded", "false");
	}
}
